import { MAX_OPTION_VALUE_CHILDREN } from '@sec-loans-ui/ifp-loans/steps/household-info/contants'
import { IfpBasicInfoFormData } from '@sec-loans-ui/ifp-loans/steps/ifp-info/types'
import { IfpWizardData } from '@sec-loans-ui/ifp-loans/wizard/types'
import { assertValue } from '@shared/helpers'
import { IfpApplicationData, InstalmentFreePeriodMonthsDurationRange } from '@shared/sec-loans'
import { OTHER_VALUE } from '@ui-common/constants'
import { YesNo } from '@ui-common/types'
import {
  bffDateStringToFormDateString,
  formDateStringToBffDateString,
  resolveBooleanInputToYesNo,
  toNumber,
} from '@ui-common/utils'

import { resolveValueFromFormFields } from '../utils/helpers'
import { IfpHouseholdInfoFormData } from './steps/household-info/types'

export const wizardDataToApplicationData = (wizardData: Partial<IfpWizardData>): Partial<IfpApplicationData> => {
  const { basicInfo, householdInfo, applicantInfos } = wizardData
  return {
    basicInfo: basicInfo && {
      durationInMonths: parseInt(basicInfo.durationInMonths),
      startsImmediately: basicInfo.startsImmediately === YesNo.Yes,
      startDate: formDateStringToBffDateString(basicInfo.startDate),
      primaryReason: basicInfo.primaryReason,
      primaryReasonOther: basicInfo.primaryReasonOther,
      hasFinancialDifficulties: basicInfo.hasFinancialDifficulties === YesNo.Yes,
    },
    householdInfo: householdInfo && resolveFormHouseholdInfo(householdInfo),
    applicantInfos: applicantInfos?.map((applicantInfo) => ({
      ...applicantInfo,
      phoneNumber: assertValue(applicantInfo.phoneNumber, 'applicantInfo.phoneNumber'),
      occupation: assertValue(applicantInfo.occupation, 'applicantInfo.occupation'),
      grossSalary: toNumber(applicantInfo.grossSalary),
      netSalary: toNumber(applicantInfo.netSalary),
    })),
  }
}

export const applicationDataToWizardData = (applicationData: Partial<IfpApplicationData>): Partial<IfpWizardData> => {
  const { basicInfo, householdInfo, applicantInfos } = applicationData
  return {
    basicInfo: basicInfo && {
      durationInMonths: basicInfo.durationInMonths.toString() as IfpBasicInfoFormData['durationInMonths'],
      durationInMonthsRange:
        basicInfo.durationInMonths <= 6
          ? InstalmentFreePeriodMonthsDurationRange.OneToSix
          : InstalmentFreePeriodMonthsDurationRange.SevenToTwelve,
      startsImmediately: assertValue(
        resolveBooleanInputToYesNo(basicInfo.startsImmediately),
        'basicInfo.startsImmediately',
      ),
      startDate: bffDateStringToFormDateString(basicInfo.startDate),
      primaryReason: basicInfo.primaryReason,
      primaryReasonOther: basicInfo.primaryReasonOther,
      hasFinancialDifficulties: assertValue(
        resolveBooleanInputToYesNo(basicInfo.hasFinancialDifficulties),
        'basicInfo.hasFinancialDifficulties',
      ),
    },
    householdInfo: householdInfo && resolveApplicationHouseholdInfo(householdInfo),
    applicantInfos: applicantInfos?.map((applicantInfo) => ({
      ...applicantInfo,
      grossSalary: applicantInfo.grossSalary?.toString(),
      netSalary: applicantInfo.netSalary?.toString(),
    })),
  }
}

const resolveFormHouseholdInfo = (householdInfo: IfpHouseholdInfoFormData): IfpApplicationData['householdInfo'] => {
  const numberOfChildren = resolveValueFromFormFields(
    assertValue(householdInfo?.numberOfChildren, 'householdInfo.numberOfChildren'),
    householdInfo.numberOfChildrenOther,
  )

  return {
    numberOfAdults: parseInt(householdInfo.numberOfAdults),
    numberOfChildren: assertValue(numberOfChildren, 'numberOfChildren'),
  }
}

const resolveApplicationHouseholdInfo = (
  householdInfo: IfpApplicationData['householdInfo'],
): IfpHouseholdInfoFormData => {
  const numberOfChildren = householdInfo.numberOfChildren
  const hasMoreThatMaxOptionValue = numberOfChildren > MAX_OPTION_VALUE_CHILDREN

  return {
    numberOfAdults: householdInfo.numberOfAdults.toString(),
    numberOfChildren: hasMoreThatMaxOptionValue ? OTHER_VALUE : `${householdInfo.numberOfChildren}`,
    numberOfChildrenOther: hasMoreThatMaxOptionValue ? numberOfChildren.toString() : undefined,
  }
}
